import { ApplicantRole, BackendFormResponse, Producer } from './api';
import { formatDate, isUnder26Years, isValidBirthday } from './date';
import { isValidZipcode } from './zipcode';
import {
    NEXT_YEAR_EFFECTIVE_DATE,
    CURRENT_YEAR_EFFECTIVE_DATE,
    IS_IT_TODAY_GREATER_THAN_OR_DEC1_AND_TILL_JAN15_RESULT,
    CURRENT_YEAR,
    NEXT_YEAR,
    SWITCH_ON_AEP,
} from './config';

export enum ISHOP_FORM_ID {
    HEALTH_PLAN = 'healthPlan',
    BLUE_ADVANTAGE = 'blueAdvantageQuoteResultForm',
    EDIT_APP = 'editAppForm',
    SAVE_QUOTE = 'saveQuoteForm',
    APPLY_FOR_PLAN = 'ApplyForPlanForm',
    BOL_FORM = 'bolForm',
    RQ_APPLY_FORM = 'rqApplyForm',
    ALT_QUOTE_KEEP = 'AltQuoteControl_keepOriginalPlan',
    ALT_CHANGE_KEEP = 'AltQuoteControl_changePlan',
    ALTERNATE_PLAN = 'SelectAlternatePlanForm',
    KEEP_PLAN = 'KeepThisPlanActionForm',
}

export function getIshopForm(formId: ISHOP_FORM_ID): HTMLFormElement | null {
    const form = document?.forms.namedItem(formId) || document.getElementById(formId);
    return form as HTMLFormElement | null;
}
export enum RELATION {
    SPOUSE = 'spouse',
    DEPENDENT = 'dependent',
}

export enum SEVERITY {
    MINIMAL = 'minimal',
    MODERATE = 'moderate',
    SIGNIFICANT = 'significant',
}

// Maps enrollmentFor values to issApp.selfQuestion codes for form submission
export const ENROLLMENT_TO_SELF_QUESTION: Record<string, string> = {
    myself: 'SELF_ONLY',
    'myself-family': 'SELF_FAM',
    'dependent-under-18': 'DEP_U18',
    'dependent-over-18': 'DEP_O18',
    'someone-else-legal': 'REP_LEGAL',
    'someone-else-no-legal': 'REP_NOLEG',
};

export interface RateQuoteMember {
    dob: string;
    zipcode?: string;
    county?: string;
    state?: string;
    relation?: RELATION;
}

export interface RateQuoteContact {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    agentUserId?: string;
}

export interface RateQuoteFormData {
    coverageStart?: string;
    qualifyingEvent?: boolean | string;
    enrollmentFor?: string;
    selfQuestion?: string;
    members: RateQuoteMember[];
    subsidyCheck?: boolean;
    houseSize?: number;
    income?: number;
    severity?: SEVERITY;
    contactInfo?: RateQuoteContact;
    producer?: Producer;
    pnumber?: string;
}

export const MAX_MEMBERS = 10;
export const MAX_DEPENDENTS = 8;

export function initRateQuoteFormData(): RateQuoteFormData {
    return {
        coverageStart: '',
        selfQuestion: '',
        members: [
            {
                dob: '',
                zipcode: '',
                county: '',
            },
        ],
    };
}

/**
 * Shape of the form model used by the standalone BlueAdvantage "applicant information" page
 * (see pages/applicant-information/index.ts). Duplicated here (rather than imported) so that
 * this util module doesn't depend on a page component.
 */
export interface ApplicantFormData {
    deliveryMethod: 'pabol' | 'email' | '';
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    zipCode: string;
    county: string;
    email: string;
    phone: string;
    includeSpouse: boolean;
    spouseFirstName: string;
    spouseLastName: string;
    spouseDateOfBirth: string;
    includeChildren: boolean;
    numberOfChildren: number;
    childrenDatesOfBirth: string[];
    householdIncome: string;
    numberOfPeopleInHousehold: number;
}

export interface TransformApplicantFormDataOptions {
    /** '1' = shopping during Open Enrollment (AEP), '2' = Qualifying Life Event (QLE). Determines coverageStart. */
    aepReasonSelection?: '1' | '2' | '';
    /** Agent producer number, if submitting on behalf of a producer (maps to RateQuoteFormData.pnumber). */
    producerNumber?: string;
    /** Agent user id, if available (maps to RateQuoteContact.agentUserId). */
    agentUserId?: string;
}

/**
 * Transforms the ApplicantFormData model (used by the BlueAdvantage applicant-information page)
 * into a RateQuoteFormData object so it can be submitted through the shared submitInputs API.
 */
export function transformApplicantFormDataToRateQuoteFormData(
    data: ApplicantFormData,
    options: TransformApplicantFormDataOptions = {}
): RateQuoteFormData {
    const { aepReasonSelection = '', producerNumber, agentUserId } = options;

    const members: RateQuoteMember[] = [
        {
            dob: data.dateOfBirth,
            zipcode: data.zipCode,
            county: data.county,
        },
    ];

    if (data.includeSpouse) {
        members.push({
            dob: data.spouseDateOfBirth,
            relation: RELATION.SPOUSE,
        });
    }

    if (data.includeChildren && data.numberOfChildren > 0) {
        data.childrenDatesOfBirth.slice(0, Math.min(data.numberOfChildren, MAX_DEPENDENTS)).forEach((dob) => {
            members.push({ dob, relation: RELATION.DEPENDENT });
        });
    }

    const contactInfo: RateQuoteContact = {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        email: data.email,
        ...(agentUserId && { agentUserId }),
    };

    const hasIncome = isNonEmptyString(data.householdIncome);
    const income = hasIncome ? Number(data.householdIncome) : undefined;

    return {
        // Open Enrollment shoppers are quoting for the upcoming plan year; QLE (or unspecified) stays on the current year.
        coverageStart: aepReasonSelection === '1' ? NEXT_YEAR : CURRENT_YEAR,
        members,
        contactInfo,
        subsidyCheck: hasIncome,
        ...(hasIncome && {
            income,
            houseSize: data.numberOfPeopleInHousehold,
        }),
        ...(producerNumber && { pnumber: producerNumber }),
    };
}

export interface ApiRateQuoteData {
    applicantDOB: string;
    zipCode: string;
    countyOfResidence: string;
    includeSpouse: boolean;
    spouseDOB?: string;
    includeChildren: boolean;
    houseHoldIncome?: string;
    numberOfPeopleInHouseHold?: string;
    includeDentalQuote: boolean;
    includeMaternityQuote: boolean;
    quoteDate: string;
    dependentDOB1?: string;
    dependentDOB2?: string;
    dependentDOB3?: string;
    dependentDOB4?: string;
    dependentDOB5?: string;
    dependentDOB6?: string;
    dependentDOB7?: string;
    dependentDOB8?: string;
    severity?: SEVERITY;
    applicantFirstName?: string;
    applicantLastName?: string;
    applicantPhone?: string;
    applicantEmail?: string;
    producerNumber?: string;
}

interface ApiRateQuoteEmbeddedData extends ApiRateQuoteData {
    applicantGender?: string;
    spouseGender?: string;
    quoteYear?: string;
}

export function transformDataForApi(
    data: RateQuoteFormData,
    isEmbeddedApi: boolean = false
): ApiRateQuoteData | URLSearchParams | null {
    const primaryMember = data.members.find((m) => m.relation !== RELATION.SPOUSE && m.relation !== RELATION.DEPENDENT);
    if (!primaryMember) return null;

    const spouseMember = data.members.find((m) => m.relation === RELATION.SPOUSE);
    const dependentMembers = data.members.filter((m) => m.relation === RELATION.DEPENDENT);

    const dependentDOBs = dependentMembers.map((member) => formatDate(member.dob));

    const coverageStartMap: Record<string, string> = {
        [CURRENT_YEAR]: IS_IT_TODAY_GREATER_THAN_OR_DEC1_AND_TILL_JAN15_RESULT
            ? NEXT_YEAR_EFFECTIVE_DATE
            : CURRENT_YEAR_EFFECTIVE_DATE,
        [NEXT_YEAR]: NEXT_YEAR_EFFECTIVE_DATE,
    };

    const quoteDate = coverageStartMap[data.coverageStart as keyof typeof coverageStartMap];

    const rateQuoteData: ApiRateQuoteData & { [key: `dependentDOB${number}`]: string | undefined } = {
        applicantDOB: formatDate(primaryMember.dob),
        zipCode: primaryMember.zipcode || '',
        countyOfResidence: primaryMember.county || '',
        includeSpouse: !!spouseMember,
        spouseDOB: spouseMember ? formatDate(spouseMember.dob) : undefined,
        includeChildren: dependentMembers.length > 0,
        includeDentalQuote: false,
        includeMaternityQuote: false,
        quoteDate,
        severity: data.severity,
        ...(data.subsidyCheck && {
            houseHoldIncome: data.income ? `${data.income}` : '0',
            numberOfPeopleInHouseHold: data.houseSize ? `${data.houseSize}` : '1',
        }),
        ...(data.contactInfo && {
            applicantFirstName: data.contactInfo.firstName,
            applicantLastName: data.contactInfo.lastName,
            applicantPhone: data.contactInfo.phone,
            applicantEmail: data.contactInfo.email,
        }),
        ...((data.pnumber || data.producer) && {
            producerNumber: data.pnumber || data?.producer?.producerNumber,
        }),
    };

    dependentDOBs.forEach((dob, index) => {
        rateQuoteData[`dependentDOB${index + 1}`] = dob;
    });

    if (isEmbeddedApi) {
        // Convert to URLSearchParams for embedded API
        const formData = new URLSearchParams();
        Object.entries(rateQuoteData).forEach(([key, value]) => {
            if (value !== undefined) {
                formData.append(key, value.toString());
            }
        });
        return formData;
    }

    // Return as ApiRateQuoteData for standard API
    return rateQuoteData as ApiRateQuoteData;
}

export function transformDataForEmbeddedApi(data: RateQuoteFormData): ApiRateQuoteEmbeddedData | null {
    const primaryMember = data.members.find((m) => m.relation !== RELATION.SPOUSE && m.relation !== RELATION.DEPENDENT);
    const spouseMember = data.members.find((m) => m.relation === RELATION.SPOUSE);
    const dependentMembers = data.members.filter((m) => m.relation === RELATION.DEPENDENT);
    if (!primaryMember) return null;
    const income = data.income;
    const houseSize = data.houseSize;
    const producerNumber = data.producer?.producerNumber;
    return {
        applicantDOB: formatDate(primaryMember.dob),
        applicantFirstName: data.contactInfo?.firstName || '',
        applicantLastName: data.contactInfo?.lastName || '',
        applicantPhone: data.contactInfo?.phone || '',
        applicantEmail: data.contactInfo?.email || '',
        zipCode: primaryMember.zipcode || '',
        countyOfResidence: primaryMember.county || '',
        // applicantGender: primaryMember.gender,
        includeSpouse: !!spouseMember,
        quoteYear: data?.coverageStart?.split('/').pop() || '',
        spouseDOB: spouseMember ? formatDate(spouseMember.dob) : undefined,
        // spouseGender: spouseMember ? spouseMember.gender : undefined,
        includeChildren: dependentMembers.length > 0,
        includeDentalQuote: false,
        includeMaternityQuote: false,
        quoteDate: data.coverageStart || '',
        dependentDOB1: dependentMembers[0] ? formatDate(dependentMembers[0].dob) : undefined,
        dependentDOB2: dependentMembers[1] ? formatDate(dependentMembers[1].dob) : undefined,
        dependentDOB3: dependentMembers[2] ? formatDate(dependentMembers[2].dob) : undefined,
        dependentDOB4: dependentMembers[3] ? formatDate(dependentMembers[3].dob) : undefined,
        dependentDOB5: dependentMembers[4] ? formatDate(dependentMembers[4].dob) : undefined,
        dependentDOB6: dependentMembers[5] ? formatDate(dependentMembers[5].dob) : undefined,
        dependentDOB7: dependentMembers[6] ? formatDate(dependentMembers[6].dob) : undefined,
        dependentDOB8: dependentMembers[7] ? formatDate(dependentMembers[7].dob) : undefined,
        houseHoldIncome: income?.toString(),
        numberOfPeopleInHouseHold: houseSize?.toString(),
        producerNumber: producerNumber,
    };
}

export function isValidForm(data: RateQuoteFormData): boolean {
    const errors: string[] = [];
    const primaryMember = data.members[0];
    const otherMembers = data.members.slice(1);

    if (!isValidBirthday(primaryMember?.dob)) {
        errors.push('Primary member has an invalid date of birth.');
    }
    if (!isValidZipcode(primaryMember?.zipcode)) {
        errors.push('Primary member has an invalid zipcode.');
    }
    if (!primaryMember?.county) {
        errors.push('Primary member county is required.');
    }
    if (primaryMember?.state?.toLowerCase() !== 'nc') {
        errors.push('Primary member must reside in North Carolina.');
    }

    if (
        otherMembers.some(
            (m) =>
                !m.dob ||
                !isValidBirthday(m.dob) ||
                !m.relation ||
                (m.relation === RELATION.DEPENDENT && !isUnder26Years(m.dob))
        )
    ) {
        errors.push('Other members must have a valid date of birth and relation.');
    }
    if (otherMembers.filter((m) => m.relation === RELATION.SPOUSE).length > 1) {
        errors.push('There can only be one spouse.');
    }

    if (otherMembers.filter((m) => m.relation === RELATION.DEPENDENT).length > MAX_DEPENDENTS) {
        errors.push(`There can only be ${MAX_DEPENDENTS} dependents.`);
    }

    if (!data.coverageStart && SWITCH_ON_AEP) {
        errors.push('Coverage start date is required.');
    }
    if (!data.qualifyingEvent && data.coverageStart !== `${NEXT_YEAR}`) {
        errors.push('Qualifying event is required.');
    }

    if (data.contactInfo) {
        if (!isNonEmptyString(data.contactInfo.firstName)) {
            errors.push('Contact first name is required.');
        } else if (!isValidName(data.contactInfo.firstName)) {
            errors.push('Contact first name contains invalid characters.');
        }
        if (!isNonEmptyString(data.contactInfo.lastName)) {
            errors.push('Contact last name is required.');
        } else if (!isValidName(data.contactInfo.lastName)) {
            errors.push('Contact last name contains invalid characters.');
        }
        if (!isValidPhoneNumber(data.contactInfo.phone)) {
            errors.push('Contact phone number is invalid.');
        }
        if (!isValidEmail(data.contactInfo.email)) {
            errors.push('Contact email is invalid.');
        }
    }
    if (data.subsidyCheck && (!data.houseSize || !data.income || isNaN(Number(data.income)))) {
        errors.push('Subsidy info is invalid.');
    }

    if (errors.length > 0) {
        console.error('Form validation errors:', errors);
        return false;
    }

    return true;
}

export const isNonEmptyString = (str?: unknown): str is string => !!(str && typeof str === 'string' && str.trim());

export const isValidName = (name: string | null | undefined) => {
    // Allow letters (including accented characters), spaces, hyphens, apostrophes, and periods
    // Reject numbers and special characters like @, #, $, %, &, *, etc.
    const pattern = /^[A-Za-z\u00C0-\u017F\s'.-]+$/;
    return isNonEmptyString(name) && pattern.test(name.trim()) && name.trim().length >= 1 && name.trim().length <= 50;
};

export const isValidPhoneNumber = (phoneNumber: string | null | undefined) => {
    const pattern = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
    return isNonEmptyString(phoneNumber) && pattern.test(phoneNumber);
};

export const isValidEmail = (email: string | null | undefined) => {
    const pattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    return isNonEmptyString(email) && pattern.test(email);
};

function normalizeCoverageStartForForm(coverageStart?: string): string {
    if (!coverageStart) return '';
    return /^\d{2}\/\d{2}\/\d{4}$/.test(coverageStart) ? coverageStart.split('/')[2] : coverageStart;
}

export function transformResponseToFormData(
    res: BackendFormResponse,
    severity?: SEVERITY,
    originalFormData?: RateQuoteFormData
): RateQuoteFormData {
    const data: RateQuoteFormData = {
        ...originalFormData,
        coverageStart: normalizeCoverageStartForForm(res.effectiveStart || originalFormData?.coverageStart),
        qualifyingEvent:
            originalFormData?.qualifyingEvent !== undefined ? originalFormData.qualifyingEvent : SWITCH_ON_AEP,
        members: [],
        severity: (res.plansSeverity as SEVERITY) || severity || originalFormData?.severity,
    };

    data.subsidyCheck = res.subsidy ? true : (originalFormData?.subsidyCheck ?? false);

    if (res.subsidy) {
        data.houseSize = Number(res.subsidy.familySize);
        data.income = Number(res.subsidy.houseHoldIncome);
    } else if (!data.subsidyCheck) {
        delete data.houseSize;
        delete data.income;
    }

    res.applicants.forEach((applicant) => {
        const member: RateQuoteMember = {
            dob: formatDate(applicant.dob, 'input'),
        };
        if (applicant.type.toLowerCase() === ApplicantRole.Primary.toLowerCase()) {
            member.zipcode = res.zipCode;
            member.county = res.county;
            member.state = 'NC';
        } else {
            member.relation =
                applicant.type.toLowerCase() === ApplicantRole.Spouse.toLowerCase()
                    ? RELATION.SPOUSE
                    : RELATION.DEPENDENT;
        }
        data.members.push(member);
    });
    return data;
}
